import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import DashboardCalendarWidget from "./components/calendar/DashboardCalendarWidget";
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert } from "react-bootstrap";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import PageTitle from './components/PageTitle';
import { API_ENDPOINTS } from './config/api';

// Custom styles for the dashboard
const dashboardStyles = `
  .stat-card-modern {
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    border: 1px solid rgba(0,0,0,0.05);
  }
  
  .stat-card-modern:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
  }
  



  .activity-timeline {
    position: relative;
    padding-left: 0;
  }

  .activity-timeline::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: repeating-linear-gradient(
      to bottom,
      var(--border-bg) 0px,
      var(--border-bg) 4px,
      transparent 4px,
      transparent 8px
    );
  }

  .activity-timeline-item {
    position: relative;
    padding-left: 48px;
    padding-bottom: 24px;
  }

  .activity-timeline-item:last-child {
    padding-bottom: 0;
  }

  .activity-timeline-icon {
    position: absolute;
    left: 0;
    top: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    border: 1px dashed var(--border-bg);
    outline: 5px solid var(--card-bg);
  }

  .activity-content {
    background: transparent;
    border-radius: 8px;
    padding: 0;
    border: none;
  }

  .activity-header {
    font-weight: 600;
    font-size: 14px;
    color: var(--gray-800);
margin-top: 4px;
    line-height: 1.2;
  }

  .activity-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #6c757d;
    margin-top: 4px;
    flex-wrap: wrap;
  }

  .activity-meta span:not(.activity-avatar) {
    color: #6c757d;
  }

  .activity-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid #e9ecef;
  }
  
  .alert-action {
    transition: all 0.2s ease-in-out;
  }
  
  .alert-action:hover {
    transform: translateY(-1px);
  }
  
`;

// Copy isProfileComplete from UsersPage
function isProfileComplete(user) {
  if (!user) return false;
  if (user.role === 'resident') {
    return (
      user.firstName &&
      user.lastName &&
      user.email &&
      user.year &&
      user.dob &&
      user.phone &&
      user.address
    );
  }
  if (user.role === 'attending') {
    return (
      user.firstName &&
      user.lastName &&
      user.email &&
      user.specialty &&
      user.dob &&
      user.phone &&
      user.address
    );
  }
  if (user.role === 'medical student') {
    return (
      user.firstName &&
      user.lastName &&
      user.email &&
      user.yearOfSchool &&
      user.dob &&
      user.phone &&
      user.address
    );
  }
  if (user.role === 'administrative assistant') {
    return (
      user.firstName &&
      user.lastName &&
      user.email &&
      user.dob &&
      user.phone &&
      user.address
    );
  }
  return (
    user.firstName &&
    user.lastName &&
    user.email
  );
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workLogStats, setWorkLogStats] = useState({ complete: 0, incomplete: 0, violation: 0 });
  const [vacationStats, setVacationStats] = useState({ pending: 0, approved: 0, denied: 0, uniqueResidents: 0 });
  const [evaluationStats, setEvaluationStats] = useState({ pending: 0, completed: 0, overdue: 0, total: 0 });
  const [residentStats, setResidentStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [attendingStats, setAttendingStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [incompleteProfileStats, setIncompleteProfileStats] = useState({ total: 0 });
  const [recentActivity, setRecentActivity] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [activityAvatars, setActivityAvatars] = useState({});
  const [caseData, setCaseData] = useState([]);
  const [caseDataLoading, setCaseDataLoading] = useState(false);
  const [totalCases, setTotalCases] = useState(0);
  const [pendingEvaluations, setPendingEvaluations] = useState([]);
  const [pendingEvaluationsLoading, setPendingEvaluationsLoading] = useState(false);
  const [formAssignmentStats, setFormAssignmentStats] = useState({ assigned: 0, completed: 0, total: 0 });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const isLikelyJwt = !!token && token.split('.').length === 3;

    if (!token || !isLikelyJwt) {
      navigate("/");
      return;
    }

    const fetchDashboardData = async () => {
      try {
        const res = await fetch(`${API_ENDPOINTS.DASHBOARD}/dashboard`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();

        if (res.ok) {
          setDashboardData(data);
          
          // Redirect to role-specific dashboard if needed
          const userRole = data.role;
          if (userRole === 'resident') {
            navigate("/resident/dashboard");
            return;
          } else if (userRole === 'attending') {
            navigate("/attending/dashboard");
            return;
          } else if (userRole === 'director') {
            // Stay on this dashboard for directors
            return;
          } else if (userRole === 'admin') {
            navigate("/admin/dashboard");
            return;
          } else if (userRole === 'student') {
            navigate("/student/dashboard");
            return;
          }
        } else {
          navigate("/");
        }
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    if (document.hidden) {
      const onVisible = () => {
        if (!document.hidden) {
          fetchDashboardData();
          document.removeEventListener('visibilitychange', onVisible);
        }
      };
      document.addEventListener('visibilitychange', onVisible);
      return () => document.removeEventListener('visibilitychange', onVisible);
    } else {
      fetchDashboardData();
    }
  }, [navigate]);

  useEffect(() => {
    if (!dashboardData || dashboardData.role !== 'director') return;
    setStatsLoading(true);
    setCaseDataLoading(true);
    setPendingEvaluationsLoading(true);
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem("token");
        const isLikelyJwt = !!token && token.split('.').length === 3;
        if (!token || !isLikelyJwt) {
          return;
        }
        const now = new Date();
        const month = now.getMonth();
        const year = now.getFullYear();
        
        let vacData = [];
        let allEvaluations = [];

        const authHeaders = { Authorization: `Bearer ${token}` };
        const summaryUrl = `${API_ENDPOINTS.DIRECTOR}/work-logs-summary?month=${month}&year=${year}`;
        const violationsUrl = `${API_ENDPOINTS.DIRECTOR}/work-hour-violations?month=${month}&year=${year}`;

        const [res, violRes] = await Promise.all([
          fetch(summaryUrl, { headers: authHeaders }),
          fetch(violationsUrl, { headers: authHeaders }),
        ]);

        let complete = 0;
        let incomplete = 0;
        let violation = 0;
        if (res.ok) {
          const data = await res.json();
          complete = data.filter((r) => r.status === 'complete').length;
          incomplete = data.filter((r) => r.status === 'incomplete').length;
        }
        if (violRes.ok) {
          const violations = await violRes.json();
          const list = Array.isArray(violations) ? violations : [];
          violation = new Set(
            list.map((v) => v.ResidentId ?? v.residentId).filter(Boolean)
          ).size;
        }
        setWorkLogStats({ complete, incomplete, violation });

        const [vacRes, evalRes, usersRes, caseRes] = await Promise.all([
          fetch(`${API_ENDPOINTS.DIRECTOR}/vacation-requests`, { headers: authHeaders }),
          fetch(`${API_ENDPOINTS.DIRECTOR}/evaluations-by-type/all`, { headers: authHeaders }),
          fetch(`${API_ENDPOINTS.DIRECTOR}/users`, { headers: authHeaders }),
          fetch(`${API_ENDPOINTS.DIRECTOR}/residents-case-summary`, { headers: authHeaders }),
        ]);

        let pending = 0;
        let approved = 0;
        let denied = 0;
        let uniqueResidents = 0;
        if (vacRes.ok) {
          vacData = await vacRes.json();
          pending = vacData.filter((r) => r.status === 'Pending').length;
          approved = vacData.filter((r) => r.status === 'Approved').length;
          denied = vacData.filter((r) => r.status === 'Denied').length;
          const residentIds = new Set(vacData.map((r) => r.residentId || (r.resident && r.resident.id)));
          uniqueResidents = residentIds.size;
        }
        setVacationStats({ pending, approved, denied, uniqueResidents });

        if (evalRes.ok) {
          const evalData = await evalRes.json();
          allEvaluations = evalData.flatMap((template) =>
            template.evaluations.map((evaluation) => ({
              ...evaluation,
              templateTitle: template.title,
              templateType: template.evaluationType,
            }))
          );

          const pendingEvalCount = allEvaluations.filter((e) => !e.responses || e.responses.length === 0).length;
          const completed = allEvaluations.filter((e) => e.responses && e.responses.length > 0).length;
          const overdue = allEvaluations.filter(
            (e) =>
              (!e.responses || e.responses.length === 0) &&
              e.dueDate &&
              new Date(e.dueDate) < new Date()
          ).length;

          setEvaluationStats({
            pending: pendingEvalCount,
            completed,
            overdue,
            total: allEvaluations.length,
          });

          const pendingEvalsList = allEvaluations
            .filter((e) => !e.responses || e.responses.length === 0)
            .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
            .slice(0, 5);
          setPendingEvaluations(pendingEvalsList);
        }

        if (usersRes.ok) {
          const usersData = await usersRes.json();
          const residents = usersData.filter(user => user.role === 'resident');
          const attendings = usersData.filter(user => user.role === 'attending');
          
          // Calculate incomplete profiles using isProfileComplete logic
          const incompleteProfiles = usersData.filter(user => !isProfileComplete(user)).length;
          setIncompleteProfileStats({ total: incompleteProfiles });
          
          setResidentStats({ 
            total: residents.length, 
            active: residents.filter(r => r.status === 'active').length,
            inactive: residents.filter(r => r.status === 'inactive').length
          });
          
          setAttendingStats({ 
            total: attendings.length, 
            active: attendings.filter(a => a.status === 'active').length,
            inactive: attendings.filter(a => a.status === 'inactive').length
          });
        }

        if (caseRes.ok) {
          const caseData = await caseRes.json();
          const total = caseData.reduce((sum, item) => sum + item.totalCases, 0);
          setTotalCases(total);
          const sortedData = caseData
            .sort((a, b) => b.totalCases - a.totalCases)
            .map((item) => ({
              name: item.name,
              cases: item.totalCases,
              year: item.year,
            }));
          setCaseData(sortedData);
        }

        const recentVacations = vacRes.ok ? vacData.slice(0, 3) : [];
        const recentEvaluations = evalRes.ok ? allEvaluations.slice(0, 2) : [];

        const activityNow = new Date();
        const recentThreshold = new Date(activityNow.getTime() - 48 * 60 * 60 * 1000);
        const currentMonthStart = new Date(activityNow.getFullYear(), activityNow.getMonth(), 1);
        const currentMonthEnd = new Date(activityNow.getFullYear(), activityNow.getMonth() + 1, 0, 23, 59, 59, 999);

        let recentWorkLogs = [];
        let recentDocuments = [];
        let recentConferenceAttendance = [];
        let recentFormSubmissions = [];
        try {
          const [workLogRes, docRes, conferencesRes, formSubsRes, onboardingAssignmentsRes] = await Promise.all([
            fetch(`${API_ENDPOINTS.DIRECTOR}/recent-work-log-submissions`, { headers: authHeaders }),
            fetch(`${API_ENDPOINTS.ONBOARDING}/submissions`, { headers: authHeaders }),
            fetch(`${API_ENDPOINTS.CONFERENCES}`, { headers: authHeaders }),
            fetch(`${API_ENDPOINTS.ONBOARDING}/form-submissions`, { headers: authHeaders }),
            fetch(`${API_ENDPOINTS.ONBOARDING}/assignments`, { headers: authHeaders }),
          ]);

          if (workLogRes.ok) {
            const workLogData = await workLogRes.json();
            const recentSubmissions = workLogData.filter((log) => {
              const updatedAt = new Date(log.submittedAt || log.updatedAt || log.createdAt);
              return updatedAt >= recentThreshold;
            });
            recentWorkLogs = recentSubmissions.slice(0, 2);
          }

          if (docRes.ok) {
            const docData = await docRes.json();
            recentDocuments = docData
              .filter(
                (doc) =>
                  doc.status === 'submitted' ||
                  doc.status === 'approved' ||
                  doc.status === 'rejected'
              )
              .sort(
                (a, b) =>
                  new Date(b.createdAt || b.reviewedAt || b.updatedAt) -
                  new Date(a.createdAt || a.reviewedAt || a.updatedAt)
              )
              .slice(0, 2);
          }

          if (conferencesRes.ok) {
            const conferencesData = await conferencesRes.json();
            const recentSessions = [];
            
            for (const conference of conferencesData) {
              if (conference.sessions) {
                for (const session of conference.sessions) {
                  const sessionDate = new Date(session.date);
                  // Only check sessions in current month
                  if (sessionDate >= currentMonthStart && sessionDate <= currentMonthEnd) {
                    // Use attendance data already included in the response if available
                    if (session.attendance && session.attendance.length > 0) {
                      // Find the most recent attendance record
                      const latestAttendance = session.attendance
                        .sort((a, b) => new Date(b.recordedAt || b.createdAt || 0) - new Date(a.recordedAt || a.createdAt || 0))[0];
                      
                      // Only include if attendance was recorded recently (last 48 hours)
                      const recordedAt = latestAttendance.recordedAt || latestAttendance.createdAt;
                      if (recordedAt && new Date(recordedAt) >= recentThreshold) {
                        recentSessions.push({
                          session: session,
                          conference: conference,
                          attendance: latestAttendance,
                          recordedAt: recordedAt
                        });
                      }
                    }
                  }
                }
              }
            }
            // Sort by recordedAt and get most recent 2
            recentConferenceAttendance = recentSessions
              .sort((a, b) => new Date(b.recordedAt) - new Date(a.recordedAt))
              .slice(0, 2);
          }

          if (formSubsRes.ok) {
            const formSubsData = await formSubsRes.json();
            recentFormSubmissions = (Array.isArray(formSubsData) ? formSubsData : [])
              .filter((s) => ['submitted', 'approved', 'rejected'].includes(String(s.status || '').toLowerCase()))
              .sort((a, b) => new Date(b.reviewedAt || b.createdAt || b.updatedAt) - new Date(a.reviewedAt || a.createdAt || a.updatedAt))
              .slice(0, 2);
          }

          if (onboardingAssignmentsRes.ok) {
            const assignmentData = await onboardingAssignmentsRes.json();
            const formAssignments = (Array.isArray(assignmentData) ? assignmentData : []).filter((a) => Boolean(a.formId));
            const completed = formAssignments.filter((a) => String(a.status).toLowerCase() === 'completed').length;
            const assigned = formAssignments.length - completed;
            setFormAssignmentStats({ assigned, completed, total: formAssignments.length });
          }
        } catch (err) {
          console.error("Error fetching recent activity feeds (work logs, documents, conferences):", err);
        }
        
        const activity = [
          ...recentVacations.map(v => ({
            type: 'vacation',
            title: `Vacation request from ${v.resident?.firstName} ${v.resident?.lastName}`,
            status: v.status,
            date: v.createdAt,
            id: v.id,
            initiator: {
              firstName: v.resident?.firstName || '',
              lastName: v.resident?.lastName || '',
              profilePic: v.resident?.profilePic || null
            },
            action: 'Added'
          })),
          ...recentEvaluations.map(e => ({
            type: 'evaluation',
            title: `${e.templateTitle} for ${e.resident?.firstName} ${e.resident?.lastName}`,
            status: e.responses && e.responses.length > 0 ? 'completed' : 'pending',
            date: e.dueDate,
            id: e.id,
            initiator: {
              firstName: e.resident?.firstName || '',
              lastName: e.resident?.lastName || '',
              profilePic: e.resident?.profilePic || null
            },
            action: 'Assigned'
          })),
          ...recentWorkLogs.map(w => ({
            type: 'worklog',
            title: `Work log submitted by ${w.resident?.firstName} ${w.resident?.lastName}`,
            status: w.status || 'submitted',
            date: w.submittedAt || w.createdAt,
            id: w.id,
            initiator: {
              firstName: w.resident?.firstName || '',
              lastName: w.resident?.lastName || '',
              profilePic: w.resident?.profilePic || null
            },
            action: 'Submitted'
          })),
          ...recentDocuments.map(doc => ({
            type: doc.status === 'approved' ? 'document-approval' : 'document-submission',
            title: doc.status === 'approved' 
              ? `${doc.document?.title || 'Document'} approved for ${doc.submitter?.firstName} ${doc.submitter?.lastName}`
              : `${doc.document?.title || 'Document'} submitted by ${doc.submitter?.firstName} ${doc.submitter?.lastName}`,
            status: doc.status,
            date: doc.reviewedAt || doc.createdAt || doc.updatedAt,
            id: doc.id,
            initiator: {
              firstName: doc.status === 'approved' 
                ? (doc.reviewer?.firstName || doc.submitter?.firstName || '')
                : (doc.submitter?.firstName || ''),
              lastName: doc.status === 'approved'
                ? (doc.reviewer?.lastName || doc.submitter?.lastName || '')
                : (doc.submitter?.lastName || ''),
              profilePic: doc.status === 'approved'
                ? (doc.reviewer?.profilePic || doc.submitter?.profilePic || null)
                : (doc.submitter?.profilePic || null)
            },
            action: doc.status === 'approved' ? 'Approved' : 'Submitted'
          })),
          ...recentFormSubmissions.map((sub) => ({
            type: sub.status === 'approved' ? 'form-approval' : 'form-submission',
            title: sub.status === 'approved'
              ? `${sub.form?.title || 'Form'} approved for ${sub.submitter?.firstName || ''} ${sub.submitter?.lastName || ''}`.trim()
              : `${sub.form?.title || 'Form'} submitted by ${sub.submitter?.firstName || ''} ${sub.submitter?.lastName || ''}`.trim(),
            status: sub.status,
            date: sub.reviewedAt || sub.createdAt || sub.updatedAt,
            id: sub.id,
            initiator: {
              firstName: sub.status === 'approved'
                ? (sub.reviewer?.firstName || sub.submitter?.firstName || '')
                : (sub.submitter?.firstName || ''),
              lastName: sub.status === 'approved'
                ? (sub.reviewer?.lastName || sub.submitter?.lastName || '')
                : (sub.submitter?.lastName || ''),
              profilePic: sub.status === 'approved'
                ? (sub.reviewer?.profilePic || sub.submitter?.profilePic || null)
                : (sub.submitter?.profilePic || null)
            },
            action: sub.status === 'approved' ? 'Approved' : 'Submitted'
          })),
          ...recentConferenceAttendance.map(att => ({
            type: 'conference-attendance',
            title: `${att.attendance.user?.firstName || 'User'} ${att.attendance.user?.lastName || ''} attended ${att.conference?.title || 'Conference'}`,
            status: att.attendance.status || 'attended',
            date: att.recordedAt,
            id: att.attendance.id || att.session.id,
            initiator: {
              firstName: att.attendance.user?.firstName || '',
              lastName: att.attendance.user?.lastName || '',
              profilePic: att.attendance.user?.profilePic || null
            },
            action: 'Attended'
          }))
        ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 8);
        
        setRecentActivity(activity);
        
      } catch (err) {
        console.error("Error fetching stats:", err);
      } finally {
        setStatsLoading(false);
        setCaseDataLoading(false);
        setPendingEvaluationsLoading(false);
      }
    };
    if (document.hidden) {
      const onVisible = () => {
        if (!document.hidden) {
          fetchStats();
          document.removeEventListener('visibilitychange', onVisible);
        }
      };
      document.addEventListener('visibilitychange', onVisible);
      return () => document.removeEventListener('visibilitychange', onVisible);
    } else {
      fetchStats();
    }
  }, [dashboardData]);

  const getStatusBadge = (status) => {
    const variants = {
      'Pending': 'warning',
      'Approved': 'success',
      'Denied': 'danger',
      'Rejected': 'danger',
      'completed': 'success',
      'pending': 'warning',
      'overdue': 'danger',
      'submitted': 'info',
      'attended': 'success',
      'rejected': 'danger',
      'approved': 'success',
      'assigned': 'secondary'
    };
    const displayStatus = status === 'submitted' ? 'Submitted' : 
                         status === 'attended' ? 'Attended' :
                         status === 'approved' ? 'Approved' :
                         status === 'rejected' ? 'Rejected' :
                         status;
    return <Badge className="rounded-2" bg={variants[status] || 'secondary'}>{displayStatus}</Badge>;
  };

  const getActivityIcon = (type) => {
    const iconStyle = { fontSize: '18px', opacity: 0.5 };
    switch (type) {
      case 'vacation':
        return <i className="bi bi-calendar-event" style={iconStyle} />;
      case 'evaluation':
        return <i className="bi bi-clipboard-check" style={iconStyle} />;
      case 'worklog':
        return <i className="bi bi-clock" style={iconStyle} />;
      case 'document-submission':
        return <i className="bi bi-file-earmark-arrow-up" style={iconStyle} />;
      case 'document-approval':
        return <i className="bi bi-file-check" style={iconStyle} />;
      case 'form-submission':
        return <i className="bi bi-ui-checks-grid" style={iconStyle} />;
      case 'form-approval':
        return <i className="bi bi-check2-square" style={iconStyle} />;
      case 'conference-attendance':
        return <i className="bi bi-people" style={iconStyle} />;
      default:
        return <i className="bi bi-clock" style={iconStyle} />;
    }
  };

  const getActivityIconBg = (type) => {
    // All icons use the same light gray background
    return 'var(--card-bg)'; // Light gray
  };

  // Function to load profile picture with authentication
  const loadProfilePicture = async (profilePicPath) => {
    if (!profilePicPath) return null;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_ENDPOINTS.PROFILE_PICS}${profilePicPath}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        return null;
      }
      
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error loading profile picture:', error);
      return null;
    }
  };

  // Function to generate fallback avatar
  const generateFallbackAvatar = (firstName, lastName) => {
    const firstInitial = firstName?.charAt(0) || 'U';
    const lastInitial = lastName?.charAt(0) || 'S';
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23007bff'/%3E%3Ctext x='50' y='50' text-anchor='middle' dy='.3em' font-size='40' fill='white'%3E${firstInitial}${lastInitial}%3C/text%3E%3C/svg%3E`;
  };

  // Load avatars for activity items
  useEffect(() => {
    const loadAvatars = async () => {
      const avatarPromises = recentActivity.map(async (activity) => {
        if (!activity.initiator) return null;
        const key = `${activity.type}-${activity.id}`;
        if (activity.initiator.profilePic) {
          const avatarUrl = await loadProfilePicture(activity.initiator.profilePic);
          return { key, url: avatarUrl || generateFallbackAvatar(activity.initiator.firstName, activity.initiator.lastName) };
        }
        return { key, url: generateFallbackAvatar(activity.initiator.firstName, activity.initiator.lastName) };
      });
      
      const results = await Promise.all(avatarPromises);
      const avatarMap = {};
      results.forEach(result => {
        if (result) {
          avatarMap[result.key] = result.url;
        }
      });
      setActivityAvatars(avatarMap);
    };

    if (recentActivity.length > 0) {
      loadAvatars();
    }
  }, [recentActivity]);

  const getEvaluationIcon = (templateType) => {
    switch (templateType?.toLowerCase()) {
      case 'performance':
        return <i className="bi bi-bar-chart-fill text-primary" />;
      case 'feedback':
        return <i className="bi bi-chat-dots-fill text-info" />;
      case 'survey':
      case 'questionnaire':
        return <i className="bi bi-clipboard-data-fill text-warning" />;
      default:
        return <i className="bi bi-clipboard-check-fill text-primary" />;
    }
  };

  const getEvaluationIconBg = (templateType) => {
    switch (templateType?.toLowerCase()) {
      case 'performance':
        return 'var(--blue-light)';
      case 'feedback':
        return 'var(--indigo-light)';
      case 'survey':
      case 'questionnaire':
        return 'var(--yellow-light)';
      default:
        return 'var(--blue-light)';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageTitle title="Loading..." />
        <Navbar pageTitle="Dashboard" />
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageTitle title="Dashboard Error" />
        <Navbar pageTitle="Dashboard" />
        <div className="p-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <p className="text-gray-600">Failed to load dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageTitle title="Director Dashboard" description="Manage your residency program and oversee operations" />
      <style>{dashboardStyles}</style>
      <Navbar pageTitle="Dashboard" />
      <Container className="py-4">
        {dashboardData.role === 'director' && (
          <>
            {/* Welcome Card with Quick Actions */}
            <Row className="mb-4">
              <Col lg={12} md={12} sm={12} className="col-12">
                <Row className="mb-4 g-3">
                          <Col lg={2} md={4} sm={4} className="col-4">
                            <Card className="shadow-sm border-0 text-start h-100 stat-card-modern">
                              <Card.Body className="p-md-3 p-2">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <div className="bg-primary-solid rounded-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, fontSize: 16 }}>
                                    <i className="bi bi-person-badge text-white" />
                                  </div>
                                  <Button
                                    variant="light-solid"
                                    size="sm"
                                    onClick={() => window.location.href = '/users#users-resident'}
                                    className="py-1 px-2"
                                  >
                                    <i className="bi bi-arrow-right-short"></i>
                                  </Button>
                                </div>
                                <h4 className="fw-bold mb-1">{residentStats.total}</h4>
                                <p className="text-muted mb-0 small">Active Residents </p>
                                {statsLoading && <div className="mt-2"><small className="text-muted">Loading...</small></div>}
                              </Card.Body>
                            </Card>
                          </Col>

                          <Col lg={2} md={4} sm={4} className="col-4">
                            <Card className="shadow-sm border-0 text-start h-100 stat-card-modern">
                              <Card.Body className="p-md-3 p-2">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <div className="bg-info-solid rounded-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, fontSize: 16 }}>
                                    <i className="bi bi-person-workspace text-white" />
                                  </div>
                                  <Button
                                    variant="light-solid"
                                    size="sm"
                                    onClick={() => window.location.href = '/users#users-attending'}
                                    className="py-1 px-2"
                                  >
                                    <i className="bi bi-arrow-right-short"></i>
                                  </Button>
                                </div>
                                <h4 className="fw-bold mb-1">{attendingStats.total}</h4>
                                <p className="text-muted mb-0 small">Active Attendings</p>
                                {statsLoading && <div className="mt-2"><small className="text-muted">Loading...</small></div>}
                              </Card.Body>
                            </Card>
                          </Col>

                          <Col lg={2} md={4} sm={4} className="col-4">
                            <Card className="shadow-sm border-0 text-start h-100 stat-card-modern">
                              <Card.Body className="p-md-3 p-2">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <div className="bg-warning-solid rounded-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, fontSize: 16 }}>
                                    <i className="bi bi-person-x-fill text-white" />
                                  </div>
                                  <Button
                                    variant="light-solid"
                                    size="sm"
                                    onClick={() => window.location.href = '/users#users-resident'}
                                    className="py-1 px-2"
                                  >
                                    <i className="bi bi-arrow-right-short"></i>
                                  </Button>
                                </div>
                                <h4 className="fw-bold mb-1">{incompleteProfileStats.total}</h4>
                                <p className="text-muted mb-0 small">Incomplete Profiles</p>
                                {statsLoading && <div className="mt-2"><small className="text-muted">Loading...</small></div>}
                              </Card.Body>
                            </Card>
                          </Col>

                          <Col lg={2} md={4} sm={4} className="col-4">
                            <Card className="shadow-sm border-0 text-start h-100 stat-card-modern">
                              <Card.Body className="p-md-3 p-2">  
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <div className="bg-danger-solid rounded-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, fontSize: 16 }}>
                                    <i className="bi bi-exclamation-triangle-fill text-white" />
                                  </div>
                                  <Button
                                    variant="light-solid"
                                    size="sm"
                                    onClick={() => navigate('/director/work-logs#tab=vacation')}
                                    className="py-1 px-2"
                                  >
                                    <i className="bi bi-arrow-right-short"></i>
                                  </Button>
                                </div>
                                <h4 className="fw-bold mb-1">{vacationStats.pending}</h4>
                                <p className="text-muted mb-0 small">Pending Requests</p>
                                {statsLoading && <div className="mt-2"><small className="text-muted">Loading...</small></div>}
                              </Card.Body>
                            </Card>
                          </Col>

                          <Col lg={2} md={4} sm={4} className="col-4">
                            <Card className="shadow-sm border-0 text-start h-100 stat-card-modern">
                              <Card.Body className="p-md-3 p-2">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <div className="bg-cyan-solid rounded-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, fontSize: 16 }}>
                                    <i className="bi bi-clipboard-check text-white" />
                                  </div>
                                  <Button
                                    variant="light-solid"
                                    size="sm"
                                    onClick={() => navigate('/evaluations?tab=assigned')}
                                    className="py-1 px-2"
                                  >
                                    <i className="bi bi-arrow-right-short"></i>
                                  </Button>
                                </div>
                                <h4 className="fw-bold mb-1">{evaluationStats.pending}</h4>
                                <p className="text-muted mb-0 small">Pending Evaluations</p>
                                {statsLoading && <div className="mt-2"><small className="text-muted">Loading...</small></div>}
                              </Card.Body>
                            </Card>
                          </Col>

                          <Col lg={2} md={4} sm={4} className="col-4">
                            <Card className="shadow-sm border-0 text-start h-100 stat-card-modern">
                              <Card.Body className="p-md-3 p-2">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <div className="bg-orange-solid rounded-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, fontSize: 16 }}>
                                    <i className="bi bi-file-earmark-text-fill text-white" />
                                  </div>
                                  <Button
                                    variant="light-solid"
                                    size="sm"
                                    className="py-1 px-2"
                                    onClick={() => navigate('/director/case-summary')}
                                  >
                                    <i className="bi bi-arrow-right-short"></i>
                                  </Button>
                                </div>
                                <h4 className="fw-bold mb-1">{totalCases}</h4>
                                <p className="text-muted mb-0 small">Total Cases</p>
                                {caseDataLoading && <div className="mt-2"><small className="text-muted">Loading...</small></div>}
                              </Card.Body>
                            </Card>
                          </Col>
                </Row>
              </Col>
                        
              <Col lg={8} md={7} sm={12}>
                <Row>
                  <Col md={12}>     
                    {/* Main Content Area */}
                    <Row>
                      <Col lg={12}>
                        {/* Statistics Cards */}
                       

                        {/* Detailed Statistics */}
                        <Row className="mb-4 g-3">
                          {/* Work Hour Logs Detail */}
                          <Col md={4}  sm={12} className="col-12">
                            <Card className="shadow-sm border-0 h-100 p-md-3 p-2">
                              <Card.Header className="border-0 d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--bs-card-bg)' }}>
                                
                                <div className="d-flex flex-column">
                                <h6 className="fw-semibold mb-0"> Work Hour Logs </h6>
                                <p className="text-muted small mb-0">Work Hour Logs status summary</p>
                                </div>
                                <Button
                                  variant="light-solid"
                                  size="sm"
                                  onClick={() => navigate('/director/work-logs#tab=work')}
                                >
                                  <i className="bi bi-arrow-right-short"></i>
                                </Button>
                              </Card.Header>
                              <Card.Body>
                                {statsLoading ? (
                                  <div className="text-center py-3">
                                    <Spinner 
                                      animation="border" 
                                      variant="primary" 
                                      size="sm"
                                      className="mb-2"
                                    />
                                    <div className="text-muted small">Loading stats...</div>
                                  </div>
                                ) : (
                                  <div className="d-flex flex-column gap-3">
                                    {(() => {
                                      const total = workLogStats.complete + workLogStats.incomplete + workLogStats.violation;
                                      const completePercent = total > 0 ? Math.round((workLogStats.complete / total) * 100) : 0;
                                      const incompletePercent = total > 0 ? Math.round((workLogStats.incomplete / total) * 100) : 0;
                                      const violationPercent = total > 0 ? Math.round((workLogStats.violation / total) * 100) : 0;
                                      
                                      return (
                                        <>
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center gap-2">
                                              <div className="rounded-circle bg-success d-flex align-items-center justify-content-center" style={{ width: 24, height: 24 }}>
                                                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '12px' }}></i>
                                              </div>
                                              <span className="text-muted small fw-semibold">Complete</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                              <b>{workLogStats.complete}</b>
                                              <Badge  bg="success-outline" className="rounded-2 py-1 px-2">{completePercent}%</Badge>
                                            </div>
                                          </div>
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center gap-2">
                                              <div className="rounded-circle bg-danger d-flex align-items-center justify-content-center" style={{ width: 24, height: 24 }}>
                                                <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '12px' }}></i>
                                              </div>
                                              <span className="text-muted small fw-semibold">Incomplete</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                              <b>{workLogStats.incomplete}</b>
                                              <Badge bg="danger-outline" className="rounded-2 py-1 px-2">{incompletePercent}%</Badge>
                                            </div>
                                          </div>
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center gap-2">
                                              <div className="rounded-circle bg-warning d-flex align-items-center justify-content-center" style={{ width: 24, height: 24 }}>
                                                <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '12px' }}></i>
                                              </div>
                                              <span className="text-muted small fw-semibold">Violations</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                              <b>{workLogStats.violation}</b>
                                              <Badge bg="warning-outline" className="rounded-2 py-1 px-2">{violationPercent}%</Badge>
                                            </div>
                                          </div>
                                        </>
                                      );
                                    })()}
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          </Col>

                          {/* Vacation Requests Detail */}
                          <Col md={4}  sm={12} className="col-12">
                            <Card className="shadow-sm border-0 h-100 p-md-3 p-2">
                              <Card.Header className="border-0 d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--bs-card-bg)' }}>
                                <div className="d-flex flex-column">
                                <h6 className="fw-semibold mb-0"> Vacation Requests </h6>
                                <p className="text-muted small mb-0">Vacation Requests status summary</p>
                                </div>
                                <Button
                                  variant="light-solid"
                                  size="sm"
                                  onClick={() => navigate('/director/work-logs#tab=vacation')}
                                >
                                  <i className="bi bi-arrow-right-short"></i>
                                </Button>
                              </Card.Header>
                              <Card.Body>
                                {statsLoading ? (
                                  <div className="text-center py-3">
                                    <Spinner 
                                      animation="border" 
                                      variant="primary" 
                                      size="sm"
                                      className="mb-2"
                                    />
                                    <div className="text-muted small">Loading activities...</div>
                                  </div>
                                ) : (
                                  <div className="d-flex flex-column gap-3">
                                    {(() => {
                                      const total = vacationStats.approved + vacationStats.pending + vacationStats.denied;
                                      const approvedPercent = total > 0 ? Math.round((vacationStats.approved / total) * 100) : 0;
                                      const pendingPercent = total > 0 ? Math.round((vacationStats.pending / total) * 100) : 0;
                                      const deniedPercent = total > 0 ? Math.round((vacationStats.denied / total) * 100) : 0;
                                      
                                      return (
                                        <>
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center gap-2">
                                              <div className="rounded-circle bg-success d-flex align-items-center justify-content-center" style={{ width: 24, height: 24 }}>
                                                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '12px' }}></i>
                                              </div>
                                              <span className="text-muted small fw-semibold">Approved</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                              <b>{vacationStats.approved}</b>
                                              <Badge bg="success-outline" className="rounded-2 py-1 px-2">{approvedPercent}%</Badge>
                                            </div>
                                          </div>
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center gap-2">
                                              <div className="rounded-circle bg-warning d-flex align-items-center justify-content-center" style={{ width: 24, height: 24 }}>
                                                <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '12px' }}></i>
                                              </div>
                                              <span className="text-muted small fw-semibold">Pending</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                              <b>{vacationStats.pending}</b>
                                              <Badge bg="warning-outline" className="rounded-2 py-1 px-2">{pendingPercent}%</Badge>
                                            </div>
                                          </div>
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center gap-2">
                                              <div className="rounded-circle bg-danger d-flex align-items-center justify-content-center" style={{ width: 24, height: 24 }}>
                                                <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '12px' }}></i>
                                              </div>
                                              <span className="text-muted small fw-semibold">Denied</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                              <b>{vacationStats.denied}</b>
                                              <Badge bg="danger-outline" className="rounded-2 py-1 px-2">{deniedPercent}%</Badge>
                                            </div>
                                          </div>
                                        </>
                                      );
                                    })()}
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col md={4} sm={12} className="col-12">
                            <Card className="shadow-sm border-0 h-100 p-md-3 p-2">
                              <Card.Header className="border-0 d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--bs-card-bg)' }}>
                                <div className="d-flex flex-column">
                                  <h6 className="fw-semibold mb-0">Form Assignments</h6>
                                  <p className="text-muted small mb-0">Onboarding form assignment status</p>
                                </div>
                                <Button
                                  variant="light-solid"
                                  size="sm"
                                  onClick={() => navigate('/onboarding?manage=forms#tab=manage&manage=forms')}
                                >
                                  <i className="bi bi-arrow-right-short"></i>
                                </Button>
                              </Card.Header>
                              <Card.Body>
                                {statsLoading ? (
                                  <div className="text-center py-3">
                                    <Spinner animation="border" variant="primary" size="sm" className="mb-2" />
                                    <div className="text-muted small">Loading stats...</div>
                                  </div>
                                ) : (
                                  <div className="d-flex flex-column gap-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div className="d-flex align-items-center gap-2">
                                        <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center" style={{ width: 24, height: 24 }}>
                                          <i className="bi bi-hourglass-split text-muted" style={{ fontSize: '12px' }}></i>
                                        </div>
                                        <span className="text-muted small fw-semibold">Assigned</span>
                                      </div>
                                      <b>{formAssignmentStats.assigned}</b>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div className="d-flex align-items-center gap-2">
                                        <div className="rounded-circle bg-success d-flex align-items-center justify-content-center" style={{ width: 24, height: 24 }}>
                                          <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '12px' }}></i>
                                        </div>
                                        <span className="text-muted small fw-semibold">Completed</span>
                                      </div>
                                      <b>{formAssignmentStats.completed}</b>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                      <span className="text-muted small fw-semibold">Total</span>
                                      <Badge bg="secondary" className="rounded-2 py-1 px-2">{formAssignmentStats.total}</Badge>
                                    </div>
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>

                        {/* Recent Activity */}
                        <Card className="shadow-sm border-0 mb-4 p-md-3 p-2">
                          <Card.Header className="border-0 d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--bs-card-bg)' }}>
                            <div className="d-flex flex-column">
                            <div className="d-flex flex-row justify-content-between align-items-center">
                            <h6 className="fw-semibold mb-0">
                              Recent Activity
                            </h6>
                            </div>
                            <p className="text-muted small mb-0">Your Recent Activity</p>
                            </div>
                          </Card.Header>
                          <Card.Body>
                            {recentActivity.length > 0 ? (
                              <div className="activity-timeline">
                                {recentActivity.map((activity, idx) => {
                                  const avatarKey = `${activity.type}-${activity.id}`;
                                  const avatarUrl = activityAvatars[avatarKey] || generateFallbackAvatar(
                                    activity.initiator?.firstName || '',
                                    activity.initiator?.lastName || ''
                                  );
                                  const formattedDate = new Date(activity.date);
                                  const timeStr = formattedDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                                  const today = new Date();
                                  const yesterday = new Date(today);
                                  yesterday.setDate(yesterday.getDate() - 1);
                                  
                                  let dateStr;
                                  if (formattedDate.toDateString() === today.toDateString()) {
                                    dateStr = 'Today';
                                  } else if (formattedDate.toDateString() === yesterday.toDateString()) {
                                    dateStr = 'Yesterday';
                                  } else {
                                    dateStr = formattedDate.toLocaleDateString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric',
                                      year: formattedDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined 
                                    });
                                  }
                                  
                                  return (
                                    <div key={idx} className="activity-timeline-item">
                                      <div className="activity-timeline-icon" style={{ backgroundColor: getActivityIconBg(activity.type) }}>
                                        {getActivityIcon(activity.type)}
                                      </div>
                                      <div className="activity-content ps-3 d-flex flex-row justify-content-between">
                                        <div className="">
                                        <div className="activity-header">
                                          {activity.title}
                                        </div>
                                        <div className="activity-meta">
                                          <span>{activity.action || 'Created'} at {timeStr}</span>
                                          <span>•</span>
                                          <span>{dateStr}</span>
                                          {activity.initiator && (
                                            <>
                                              <span>by:</span>
                                              {/*<img 
                                                src={avatarUrl} 
                                                alt={`${activity.initiator.firstName} ${activity.initiator.lastName}`}
                                                className="activity-avatar"
                                              />  */}
                                              <span>{activity.initiator.firstName} {activity.initiator.lastName}</span>
                                            </>
                                          )}
                                        </div>
                                        </div>
                                        <div className="mt-2">
                                          {getStatusBadge(activity.status)}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-center py-3 text-muted">
                                No recent activity
                              </div>
                            )}
                          </Card.Body>
                        </Card>

                        {/* Pending Evaluations */}
                        <Card className="shadow-sm border-0 mb-4 p-md-3 p-2">
                          <Card.Header className=" border-0 d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--bs-card-bg)' }}>
                            <div className="d-flex flex-column">
                            <div className="d-flex flex-row justify-content-start align-items-center">
                            <h6 className="fw-semibold mb-0">
                              Pending Evaluations
                  
                            </h6>            
                            {pendingEvaluations.length > 0 && (
                                <Badge bg="danger" className="ms-2">{pendingEvaluations.length}</Badge>
                              )}
                              </div>
                            <p className="text-muted small mb-0">Your Pending Assigned evaluations</p>
                            </div>
                            <Button
                              variant="light-solid"
                              size="sm"
                              onClick={() => navigate('/evaluations?tab=assigned')}
                            >
                              <i className="bi bi-arrow-right-short"></i>
                            </Button>
                          </Card.Header>
                          <Card.Body>
                            {pendingEvaluationsLoading ? (
                              <div className="text-center py-4">
                                <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                                Loading evaluations...
                              </div>
                            ) : pendingEvaluations.length > 0 ? (
                              <div className="d-flex flex-column gap-2">
                                {pendingEvaluations.map((evaluation, idx) => (
                                  <div 
                                    key={idx} 
                                    className="d-flex flex-row custom-hover-bg-alt justify-content-between align-items-center p-md-3 p-2 border-alt-light rounded"
                                    onClick={() => navigate(`/evaluations/${evaluation.id}/complete`)}
                                  >
                                    <div className="d-flex flex-row  justify-content-start align-items-center gap-2">
                                    <div className="d-flex align-items-center justify-content-center">
                                      <div className="d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, backgroundColor: getEvaluationIconBg(evaluation.templateType), borderRadius: 6 }}>
                                        {getEvaluationIcon(evaluation.templateType)}
                                      </div>
                                    </div>
                                    <div className="d-flex flex-column">
                                      <div className="fw-semibold small">
                                        {evaluation.templateTitle} - {evaluation.resident?.firstName} {evaluation.resident?.lastName}
                                      </div>
                                      <div className="text-muted small">
                                        Due: {new Date(evaluation.dueDate).toLocaleDateString()}
                                        {evaluation.templateType && (
                                          <span className="ms-2">• {evaluation.templateType}</span>
                                        )}
                                      </div>
                                    </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-end">
                                      {new Date() > new Date(evaluation.dueDate) ? (
                                        <Badge bg="danger" className=" rounded-2 text-danger fw-bold">
                                          <i className="bi bi-clock d-md-none d-block"></i><span className="ms-1 d-none d-md-block">Overdue</span>
                                        </Badge>
                                      ) : (
                                        <Badge bg="success" className=" rounded-2 text-success fw-bold">
                                          <i className="bi bi-clock d-md-none d-block"></i><span className="ms-1 d-none d-md-block">Active</span>
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-3 text-muted">
                                No pending evaluations
                              </div>
                            )}
                          </Card.Body>
                        </Card>

                        {/* Case Count Chart */}
                        <Card className="shadow-sm border-0 mb-4 p-md-3 p-2">
                          <Card.Header className="border-0 d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--bs-card-bg)' }}>
                            <div className="d-flex flex-column">
                            <h6 className="fw-semibold mb-0">  Cases per Resident </h6>
                            <p className="text-muted small mb-0">Your Case Count by Resident</p>
                            </div>
                            <Button
                              variant="light-solid"
                              size="sm"
                              onClick={() => navigate('/director/case-summary')}
                            >
                              <i className="bi bi-arrow-right-short"></i>
                            </Button>
                          </Card.Header>
                          <Card.Body>
                            {caseDataLoading ? (
                              <div className="text-center py-4">
                                <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                                Loading case data...
                              </div>
                            ) : caseData.length > 0 ? (
                              <div style={{ height: '300px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart
                                    data={caseData}
                                    margin={{
                                      top: 0,
                                      right: 0,
                                      left: 0,
                                      bottom: 0,
                                    }}
                                  >
                                    <CartesianGrid strokeDasharray="2 6" vertical={false} stroke="var(--border-bg)" />
                                    <XAxis 
                                      dataKey="name" 
                                      angle={-45}
                                      textAnchor="end"
                                      height={80}
                                      interval={0}
                                      axisLine={{stroke: 'var(--border-bg)'}}
                                      tickLine={false}
                                      tick={{ fontSize: '0.75rem', fill: 'var(--gray-600)', fontWeight: 600 }}
                                    />
                                    <YAxis axisLine={{stroke: 'var(--border-bg)'}} tickLine={false} tick={{ fontSize: '0.875rem', fill: 'var(--gray-600)' }} />
                                    <Tooltip 
                                      formatter={(value, name) => [value, 'Cases']}
                                      labelFormatter={(label) => `${label} (PGY-${caseData.find(item => item.name === label)?.year || 'N/A'})`}
                                      contentStyle={{
                                        backgroundColor: 'var(--card-bg)',
                                        border: '1px solid var(--border-bg)',
                                        borderRadius: '6px',
                                        boxShadow: 'var(--box-shadow-sm)',
                                        padding: '8px 12px',
                                        fontSize: '0.875rem'
                                      }}
                                      cursor={{ fill: 'var(--gray-100)' }}
                                    />
                                    <Bar dataKey="cases" fill="var(--indigo)" radius={[6, 6, 0, 0]} barSize={caseData.length > 10 ? 20 : 30}>
                                      {caseData.map((entry, index) => (
                                        <Cell 
                                          key={`cell-${index}`} 
                                          fill={`var(--indigo)`}
                                        />
                                      ))}
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            ) : (
                              <div className="text-center py-4 text-muted">
                                No case data available
                              </div>
                            )}
                          </Card.Body>
                        </Card>

                        {/* Alerts Section 
                        {(workLogStats.violation > 0 || evaluationStats.overdue > 0 || vacationStats.pending > 0) && (
                          <div className="d-flex flex-column gap-2 mb-4">
                            {workLogStats.violation > 0 && (
                              <Alert variant="warning" className="mb-0">
                                <FaExclamationTriangle className="me-2" />
                                <strong>Work Hour Violations:</strong> {workLogStats.violation} resident(s) have exceeded weekly hour limits.
                                <Button 
                                  variant="outline-warning" 
                                  size="sm" 
                                  className="ms-2 alert-action"
                                  onClick={() => navigate('/director/work-logs#tab=work')}
                                >
                                  Review
                                </Button>
                              </Alert>
                            )}
                            {evaluationStats.overdue > 0 && (
                              <Alert variant="danger" className="mb-0">
                                <FaClipboardCheck className="me-2" />
                                <strong>Overdue Evaluations:</strong> {evaluationStats.overdue} evaluation(s) are past due.
                                <Button 
                                  variant="outline-danger" 
                                  size="sm" 
                                  className="ms-2 alert-action"
                                  onClick={() => navigate('/evaluations?tab=assigned')}
                                >
                                  Review
                                </Button>
                              </Alert>
                            )}
                            {vacationStats.pending > 0 && (
                              <Alert variant="info" className="mb-0">
                                <FaUmbrellaBeach className="me-2" />
                                <strong>Pending Requests:</strong> {vacationStats.pending} vacation request(s) await approval.
                                <Button 
                                  variant="outline-info" 
                                  size="sm" 
                                  className="ms-2 alert-action"
                                  onClick={() => navigate('/director/work-logs#tab=vacation')}
                                >
                                  Review
                                </Button>
                              </Alert>
                            )}
                          </div>
                        )}
                          */}
                      </Col>
                    </Row>
                  </Col>
                </Row>
            </Col>
            <Col  lg={4} md={5} sm={12} className="col-12">
                <div className="d-flex flex-column gap-3">
                  <Col md={12} className=" d-none d-lg-block d-md-none d-sm-none ">
                  <Card className="shadow-sm border-0 p-4">
                    <h6 className="fw-semibold mb-3">Quick Actions</h6>
                    <div className="d-grid gap-2 quick-actions-container">
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => navigate('/evaluations/create')}
                      >
                        <div className="d-flex flex-column align-items-center">
                        <i className="bi bi-plus-circle-fill text-primary fs-5"/>
                        <span>Create Evaluation</span>
                        </div>
                      </Button>
                      <Button 
                        variant="outline-success" 
                        size="sm"
                        onClick={() => navigate('/director/work-logs')}
                      >
                        <div className="d-flex flex-column align-items-center">
                        <i className="bi bi-people-fill text-success fs-5" />
                        <span>View Work Logs</span>
                        </div>
                      </Button>
                      <Button 
                        variant="outline-info" 
                        size="sm"
                        onClick={() => navigate('/evaluations?tab=assigned')}
                      >
                        <div className="d-flex flex-column align-items-center">
                        <i className="bi bi-clipboard-check-fill text-info fs-5" />
                        <span>Pending Evaluations</span>
                        </div>
                      </Button>
                      {/*<Button 
                        variant="outline-warning" 
                        size="sm"
                        onClick={() => navigate('/director/case-summary')}
                      >
                        <div className="d-flex flex-column align-items-center ">
                        <i className="bi bi-card-heading text-warning fs-5" />
                        <span>View Case Logs</span>
                        </div>
                      </Button>*/}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => navigate('/onboarding/forms/new')}
                      >
                        <div className="d-flex flex-column align-items-center ">
                          <i className="bi bi-ui-checks-grid text-muted fs-5" />
                          <span>Create Forms</span>
                        </div>
                      </Button>
                    </div>
                  </Card>
                  </Col>
                  <Col md={12}>
                    <DashboardCalendarWidget />
                  </Col>
                </div>
            </Col>
          </Row>

           
          </>
        )}
      </Container>
    </div>
  );
};

export default Dashboard;
